import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Page from '../../../componentes/plantilla/Page';
import Card from '../../../componentes/contenedores/Card';
import { PlanillaServicio } from '../../../servicios/planillaServicio';
import { mostraAlerta, mostraAlertaOk, mostraAlertaPregunta } from '../../../componentes/alerts/sweetAlert';
import { NegocioConfig } from '../../../configuracion/Empresa';

const DetallePlanilla = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [periodo, setPeriodo] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [mostrarModalRecibo, setMostrarModalRecibo] = useState(false);

  const voucherRef = useRef();

  const cargarDatos = async () => {
    try {
      setCargando(true);
      const data = await PlanillaServicio.obtenerDetallePeriodo(id);
      if (!data) {
        mostraAlerta('Período de planilla no encontrado', 'error');
        navigate('/app/admin/planillas');
        return;
      }
      setPeriodo(data.periodo);
      setDetalles(data.detalles);
    } catch (err) {
      console.error(err);
      mostraAlerta('Error al cargar detalle de la planilla', 'error');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const handleAprobar = () => {
    mostraAlertaPregunta(
      async (confirmado) => {
        if (confirmado) {
          try {
            await PlanillaServicio.aprobarPlanilla(id);
            mostraAlertaOk('La planilla fue aprobada con éxito');
            cargarDatos();
          } catch (err) {
            mostraAlerta('Error al aprobar', 'error');
          }
        }
      },
      `¿Desea aprobar la planilla "${periodo?.descripcion}"? Se actualizarán saldos de préstamos.`
    );
  };

  const handlePagar = () => {
    mostraAlertaPregunta(
      async (confirmado) => {
        if (confirmado) {
          try {
            await PlanillaServicio.pagarPlanilla(id);
            mostraAlertaOk('La planilla ha sido marcada como Pagada');
            cargarDatos();
          } catch (err) {
            mostraAlerta('Error al registrar pago', 'error');
          }
        }
      },
      `¿Confirmar que la planilla "${periodo?.descripcion}" fue cancelada a todos los colaboradores?`
    );
  };

  const abrirRecibo = (detalle) => {
    setEmpleadoSeleccionado(detalle);
    setMostrarModalRecibo(true);
  };

  const cerrarRecibo = () => {
    setMostrarModalRecibo(false);
    setEmpleadoSeleccionado(null);
  };

  const imprimirRecibo = () => {
    window.print();
  };

  const formatLempiras = (val) =>
    `L ${Number(val || 0).toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const datosPage = {
    titulo: {
      titulo: periodo ? periodo.descripcion : 'Detalle de Planilla',
      url: `/app/admin/planillas/detalle/${id}`,
      tituloUrl: 'Planillas',
      nombreUrl: 'Detalle',
    },
  };

  if (cargando) {
    return (
      <Page datos={datosPage}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="text-muted mt-2">Cargando desglose de planilla...</p>
        </div>
      </Page>
    );
  }

  return (
    <Page datos={datosPage}>
      {/* Botones de Navegación y Acciones Rápidas */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2 no-print">
        <Link to="/app/admin/planillas" className="btn btn-outline-secondary btn-sm">
          <i className="fas fa-arrow-left mr-1"></i> Volver a Planillas
        </Link>
        <div className="d-flex gap-2">
          {periodo?.estado === 'BORRADOR' && (
            <button className="btn btn-success shadow-sm font-weight-bold" onClick={handleAprobar}>
              <i className="fas fa-check-circle mr-1"></i> Aprobar Planilla
            </button>
          )}
          {periodo?.estado === 'APROBADA' && (
            <button className="btn btn-primary shadow-sm font-weight-bold" onClick={handlePagar}>
              <i className="fas fa-dollar-sign mr-1"></i> Marcar como Pagada
            </button>
          )}
          <button className="btn btn-outline-primary shadow-sm" onClick={() => window.print()}>
            <i className="fas fa-print mr-1"></i> Imprimir Planilla Completa
          </button>
        </div>
      </div>

      {/* Encabezado del Período */}
      <div className="card shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-start border-bottom pb-3 mb-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="badge badge-secondary border font-monospace px-2 py-1" style={{ fontSize: '0.9rem' }}>
                  {periodo?.codigo}
                </span>
                <span
                  className={`badge px-2 py-1 ${
                    periodo?.estado === 'APROBADA'
                      ? 'badge-success'
                      : periodo?.estado === 'PAGADA'
                      ? 'badge-primary'
                      : 'badge-warning'
                  }`}
                >
                  {periodo?.estado}
                </span>
                <span className="badge badge-secondary px-2 py-1">
                  {periodo?.tipoPeriodo}
                </span>
              </div>
              <h3 className="font-weight-bold mb-1">{periodo?.descripcion}</h3>
              <p className="text-muted mb-0 small">
                <i className="fas fa-calendar-alt mr-1"></i> Período del {periodo?.fechaInicio} al {periodo?.fechaFin}
                {periodo?.fechaPago && ` | Pagada el: ${periodo.fechaPago}`}
              </p>
            </div>
            <div className="text-right">
              <span className="text-muted text-uppercase small font-weight-bold">Total Desembolsado</span>
              <h2 className="text-primary font-weight-bold my-0" style={{ fontSize: '2rem' }}>
                {formatLempiras(periodo?.totalNeto)}
              </h2>
              <span className="badge badge-secondary border text-muted">
                {periodo?.totalEmpleados} colaboradores incluidos
              </span>
            </div>
          </div>

          {/* Cajas de resumen */}
          <div className="row text-center pt-2">
            <div className="col-md-3 col-6 mb-2">
              <span className="text-muted small text-uppercase font-weight-bold">Salario Base Fijo</span>
              <div className="h5 font-weight-bold my-1">{formatLempiras(periodo?.totalBruto)}</div>
            </div>
            <div className="col-md-3 col-6 mb-2">
              <span className="text-success small text-uppercase font-weight-bold">Total Comisiones</span>
              <div className="h5 font-weight-bold text-success my-1">+{formatLempiras(periodo?.totalComisiones)}</div>
            </div>
            <div className="col-md-3 col-6 mb-2">
              <span className="text-danger small text-uppercase font-weight-bold">Total Retenciones</span>
              <div className="h5 font-weight-bold text-danger my-1">-{formatLempiras(periodo?.totalDeducciones)}</div>
            </div>
            <div className="col-md-3 col-6 mb-2">
              <span className="text-primary small text-uppercase font-weight-bold">Total Neto a Pagar</span>
              <div className="h5 font-weight-bold text-primary my-1">{formatLempiras(periodo?.totalNeto)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla Desglosada de Empleados */}
      <Card titulo="Nómina Individual por Colaborador">
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th>Colaborador / Cargo</th>
                <th>Modalidad</th>
                <th className="text-right">Sueldo Base</th>
                <th className="text-right">Ventas</th>
                <th className="text-center">%</th>
                <th className="text-right">Comisión</th>
                <th className="text-right">IHSS</th>
                <th className="text-right">RAP</th>
                <th className="text-right">Préstamo</th>
                <th className="text-right">Retenciones</th>
                <th className="text-right font-weight-bold">Neto a Pagar</th>
                <th className="text-center no-print">Comprobante</th>
              </tr>
            </thead>
            <tbody>
              {detalles.map((det) => (
                <tr key={det.empleadoId}>
                  <td>
                    <div className="font-weight-bold">{det.nombreEmpleado}</div>
                    <small className="text-muted">{det.cargo}</small>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        det.modalidad === 'SUELDO_BASE'
                          ? 'badge-secondary'
                          : det.modalidad === 'BASE_MAS_COMISION'
                          ? 'badge-success'
                          : 'badge-warning'
                      }`}
                    >
                      {det.modalidad === 'SUELDO_BASE'
                        ? 'Base Fijo'
                        : det.modalidad === 'BASE_MAS_COMISION'
                        ? 'Base + Com.'
                        : 'Solo Comisión'}
                    </span>
                  </td>
                  <td className="text-right">{formatLempiras(det.salarioBasePeriodo)}</td>
                  <td className="text-right text-muted">
                    {det.ventasPeriodo > 0 ? formatLempiras(det.ventasPeriodo) : '—'}
                  </td>
                  <td className="text-center text-muted">
                    {det.tasaComision > 0 ? `${det.tasaComision}%` : '—'}
                  </td>
                  <td className="text-right text-success font-weight-bold">
                    {det.montoComision > 0 ? formatLempiras(det.montoComision) : '—'}
                  </td>
                  <td className="text-right text-muted">{formatLempiras(det.deduccionIHSS)}</td>
                  <td className="text-right text-muted">{formatLempiras(det.deduccionRAP)}</td>
                  <td className="text-right text-danger">
                    {det.deduccionPrestamo > 0 ? `-${formatLempiras(det.deduccionPrestamo)}` : '—'}
                  </td>
                  <td className="text-right text-danger font-weight-bold">
                    -{formatLempiras(det.totalDeducciones)}
                  </td>
                  <td className="text-right text-primary font-weight-bold" style={{ fontSize: '0.98rem' }}>
                    {formatLempiras(det.sueldoNeto)}
                  </td>
                  <td className="text-center no-print">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      title="Ver / Imprimir Recibo"
                      onClick={() => abrirRecibo(det)}
                    >
                      <i className="fas fa-file-invoice mr-1"></i> Recibo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="font-weight-bold">
              <tr>
                <td colSpan="2" className="text-uppercase">Totales</td>
                <td className="text-right">{formatLempiras(periodo?.totalBruto)}</td>
                <td colSpan="2"></td>
                <td className="text-right text-success">{formatLempiras(periodo?.totalComisiones)}</td>
                <td colSpan="3"></td>
                <td className="text-right text-danger">-{formatLempiras(periodo?.totalDeducciones)}</td>
                <td className="text-right text-primary" style={{ fontSize: '1.05rem' }}>
                  {formatLempiras(periodo?.totalNeto)}
                </td>
                <td className="no-print"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Modal: Recibo de Pago Individual (Váucher de Nómina) */}
      {mostrarModalRecibo && empleadoSeleccionado && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)' }}
          role="dialog"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-primary text-white no-print">
                <h5 className="modal-title font-weight-bold">
                  <i className="fas fa-receipt mr-2"></i> Comprobante de Pago de Nómina
                </h5>
                <button
                  type="button"
                  className="close text-white"
                  onClick={cerrarRecibo}
                  aria-label="Cerrar"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body p-4" ref={voucherRef} id="voucher-imprimible">
                {/* Membrete del Recibo */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={NegocioConfig.logo}
                      alt="Logo Aressa"
                      style={{ height: '50px', width: 'auto' }}
                    />
                    <div>
                      <h4 className="font-weight-bold text-primary mb-0">{NegocioConfig.nombre}</h4>
                      <small className="text-muted d-block">SISTEMA INTEGRADO SIGVEM — CONTROL DE PLANILLA</small>
                      <small className="text-muted">RTN: 08019995123456 | Tegucigalpa, Honduras</small>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="badge badge-secondary border px-2 py-1 font-monospace">
                      {periodo?.codigo}
                    </span>
                    <h5 className="font-weight-bold my-1">RECIBO DE PAGO</h5>
                    <small className="text-muted d-block">Fecha: {new Date().toLocaleDateString('es-HN')}</small>
                  </div>
                </div>

                {/* Datos del Colaborador */}
                <div className="p-3 rounded mb-3 border">
                  <div className="row">
                    <div className="col-sm-6 mb-1">
                      <span className="text-muted small d-block">Nombre del Colaborador:</span>
                      <strong>{empleadoSeleccionado.nombreEmpleado}</strong>
                    </div>
                    <div className="col-sm-3 mb-1">
                      <span className="text-muted small d-block">Cargo / Puesto:</span>
                      <strong>{empleadoSeleccionado.cargo}</strong>
                    </div>
                    <div className="col-sm-3 mb-1">
                      <span className="text-muted small d-block">Modalidad:</span>
                      <span className="badge badge-info">{empleadoSeleccionado.modalidad}</span>
                    </div>
                    <div className="col-sm-6 mt-2">
                      <span className="text-muted small d-block">Período de Liquidación:</span>
                      <strong>{periodo?.descripcion}</strong> ({periodo?.fechaInicio} al {periodo?.fechaFin})
                    </div>
                    <div className="col-sm-6 mt-2">
                      <span className="text-muted small d-block">Frecuencia:</span>
                      <strong>{periodo?.tipoPeriodo}</strong>
                    </div>
                  </div>
                </div>

                {/* Tabla de Ingresos y Deducciones a 2 Columnas */}
                <div className="row mb-3">
                  {/* Ingresos */}
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div className="border rounded h-100 p-3">
                      <h6 className="font-weight-bold text-success border-bottom pb-2 mb-2">
                        <i className="fas fa-plus-circle mr-1"></i> INGRESOS (HABERES)
                      </h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td>Salario Base Período:</td>
                            <td className="text-right font-weight-bold">
                              {formatLempiras(empleadoSeleccionado.salarioBasePeriodo)}
                            </td>
                          </tr>
                          {empleadoSeleccionado.montoComision > 0 && (
                            <tr>
                              <td>
                                Comisiones por Ventas ({empleadoSeleccionado.tasaComision}%):
                                <small className="text-muted d-block">
                                  Base ventas: {formatLempiras(empleadoSeleccionado.ventasPeriodo)}
                                </small>
                              </td>
                              <td className="text-right text-success font-weight-bold align-middle">
                                +{formatLempiras(empleadoSeleccionado.montoComision)}
                              </td>
                            </tr>
                          )}
                          {empleadoSeleccionado.bonificaciones > 0 && (
                            <tr>
                              <td>Bonificaciones / Incentivos:</td>
                              <td className="text-right text-success font-weight-bold">
                                +{formatLempiras(empleadoSeleccionado.bonificaciones)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="border-top font-weight-bold">
                          <tr>
                            <td className="pt-2">TOTAL INGRESOS:</td>
                            <td className="text-right pt-2 text-success">
                              {formatLempiras(empleadoSeleccionado.totalIngresos)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Deducciones */}
                  <div className="col-md-6">
                    <div className="border rounded h-100 p-3">
                      <h6 className="font-weight-bold text-danger border-bottom pb-2 mb-2">
                        <i className="fas fa-minus-circle mr-1"></i> DEDUCCIONES (RETENCIONES)
                      </h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td>IHSS (Seguro Social):</td>
                            <td className="text-right text-muted font-weight-bold">
                              {formatLempiras(empleadoSeleccionado.deduccionIHSS)}
                            </td>
                          </tr>
                          <tr>
                            <td>RAP (Aportación Laboral):</td>
                            <td className="text-right text-muted font-weight-bold">
                              {formatLempiras(empleadoSeleccionado.deduccionRAP)}
                            </td>
                          </tr>
                          {empleadoSeleccionado.deduccionPrestamo > 0 && (
                            <tr>
                              <td>Cuota Préstamo / Anticipo:</td>
                              <td className="text-right text-danger font-weight-bold">
                                -{formatLempiras(empleadoSeleccionado.deduccionPrestamo)}
                              </td>
                            </tr>
                          )}
                          {empleadoSeleccionado.otrasDeducciones > 0 && (
                            <tr>
                              <td>Otras Deducciones:</td>
                              <td className="text-right text-danger font-weight-bold">
                                -{formatLempiras(empleadoSeleccionado.otrasDeducciones)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="border-top font-weight-bold">
                          <tr>
                            <td className="pt-2">TOTAL DEDUCCIONES:</td>
                            <td className="text-right pt-2 text-danger">
                              -{formatLempiras(empleadoSeleccionado.totalDeducciones)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Gran Total Neto a Percibir */}
                <div className="bg-primary text-white p-3 rounded d-flex justify-content-between align-items-center mb-4 shadow-sm">
                  <span className="font-weight-bold text-uppercase" style={{ fontSize: '1.1rem' }}>
                    NETO A PERCIBIR:
                  </span>
                  <span className="font-weight-bold" style={{ fontSize: '1.6rem' }}>
                    {formatLempiras(empleadoSeleccionado.sueldoNeto)}
                  </span>
                </div>

                {/* Bloque de Firmas */}
                <div className="row mt-5 pt-3">
                  <div className="col-6 text-center">
                    <div style={{ borderTop: '1px solid #94a3b8', margin: '0 auto', width: '80%' }}></div>
                    <small className="font-weight-bold d-block mt-1">GRUPO ARESSA S. DE R.L.</small>
                    <small className="text-muted">Firma y Sello de Recursos Humanos</small>
                  </div>
                  <div className="col-6 text-center">
                    <div style={{ borderTop: '1px solid #94a3b8', margin: '0 auto', width: '80%' }}></div>
                    <small className="font-weight-bold d-block mt-1">{empleadoSeleccionado.nombreEmpleado}</small>
                    <small className="text-muted">Firma y Huella de Recibido Conforme</small>
                  </div>
                </div>
              </div>

              <div className="modal-footer no-print">
                <button type="button" className="btn btn-secondary" onClick={cerrarRecibo}>
                  Cerrar
                </button>
                <button type="button" className="btn btn-primary" onClick={imprimirRecibo}>
                  <i className="fas fa-print mr-1"></i> Imprimir Comprobante
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default DetallePlanilla;
